/*
:Page: Modify Passenger Travel Insurance
*/
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  processTravelCPR,
  transformVistaCPRData,
} from "../../../../api/cprServices";
import {
  deleteTravelMember,
  getTravelMembers,
  updateTravelMembers,
} from "../../../../api/quoteServices";
import Button from "../../../../components/common/Button/Button";
import Input from "../../../../components/common/Input/Input";
import FileUploadInput from "../../../../components/common/newFileUploadInput/FileUploadInput";
import SelectInput from "../../../../components/common/selectInput/selectInput";
import SingleDatePicker from "../../../../components/common/SingleDatePicker/SingleDatePicker";
import FormTabs from "../../../../components/common/tabs/formTabs";
import TravelDetailsTable from "../../../../components/common/TravelDetailsTable/TravelDetailsTable";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import TravelBottomStickyBtnLayout from "../../../../components/newTravelPolicy/layouts/TravelBottomStickyBtnLayout";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import {
  fetchNationalitiesList,
  selectNationalities,
  selectNationalitiesLoading,
} from "../../../../redux/features/constants/nationalitiesSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { PolicyViewResponse } from "../../../../redux/features/motor/getQuoteSlice";
import {
  fetchProfileInfo,
  selectProfileData,
} from "../../../../redux/features/profileSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";

// Gender tabs
const genderTabs = [
  { label_en: "Male", label_ar: "ذكر", type: "Male" },
  { label_en: "Female", label_ar: "أنثى", type: "Female" },
];

// Relationship options for family members (labels set dynamically via translate)
const RELATIONSHIP_OPTIONS_DATA = [
  { code: "SPOUSE", value: "SPOUSE", label_en: "Spouse", label_ar: "زوج أو زوجة" },
  { code: "CHILD", value: "CHILD", label_en: "Child", label_ar: "ابن أو ابنة" },
];

const ModifyPassengerTravel = () => {
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const { policy_id } = useParams();

  // Build translated relationship options
  const relationshipOptions = RELATIONSHIP_OPTIONS_DATA.map((opt) => ({
    ...opt,
    label: translate(opt.label_en, opt.label_ar),
  }));

  // Navigation function to policy overview
  const handleNavigationToPolicyOverview = () => {
    // Navigate to policy overview with ManagePolicy tab - using window.history.back() since policy_id might be encrypted
    window.history.back();
  };

  // Redux selectors
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const policyDetails = useSelector(PolicyViewResponse);
  const nationalityOptions = useSelector(selectNationalities);
  const nationalitiesLoading = useSelector(selectNationalitiesLoading);
  const profileData = useSelector(selectProfileData);

  // State management
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [primaryMember, setPrimaryMember] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [originalMembers, setOriginalMembers] = useState({}); // Track original values for change detection
  const [modifiedMembers, setModifiedMembers] = useState({}); // Track which members have changes
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingMemberCode, setDeletingMemberCode] = useState(null); // Track which member is being deleted
  const [touchedFields, setTouchedFields] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Mobile detection state
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection - Enhanced for DevTools testing
  useEffect(() => {
    const checkMobile = () => {
      const isMobileViewport = window.innerWidth <= 768;
      const isTouchDevice = "ontouchstart" in window;
      const isMobileUserAgent =
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      // Consider it mobile if viewport is small OR it's a mobile user agent
      // This helps with DevTools mobile simulation
      const isMobileDevice =
        isMobileViewport &&
        (isTouchDevice ||
          isMobileUserAgent ||
          window.navigator.maxTouchPoints > 0);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch profile info and nationalities on component mount
  useEffect(() => {
    if (!profileData && loginTokenInfo?.token) {
      dispatch(fetchProfileInfo());
    }
  }, [profileData, dispatch, loginTokenInfo?.token]);

  useEffect(() => {
    if (nationalityOptions.length === 0 && !nationalitiesLoading) {
      dispatch(fetchNationalitiesList());
    }
  }, [dispatch, nationalityOptions.length, nationalitiesLoading]);

  // Load members data when component mounts
  useEffect(() => {
    if (policy_id) {
      loadTravelMembers();
    }
  }, [policy_id]);

  // Helper function to format profile date - same as contact details
  const formatProfileDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Real logged-in user data from profile - same as contact details
  const loggedInUserData = {
    cprNumber: profileData?.CPR || "",
    fullName: profileData
      ? `${profileData.FirstName || ""} ${profileData.LastName || ""}`.trim()
      : "",
    nationality: profileData?.Nationality || "Bahraini",
    gender: profileData?.Gender || "Male",
    dateOfBirth: profileData?.DateOfBirth
      ? formatProfileDate(profileData.DateOfBirth)
      : "",
    email: profileData?.Email || "",
    mobile: profileData?.PhoneNumber || "",
  };

  // Load travel members from API (correct approach)
  const loadTravelMembers = async () => {
    setMembersLoading(true);
    try {
      console.log("🚀 Loading travel members for policy:", policy_id);

      const response = await getTravelMembers(policy_id);
      console.log("📋 Travel members response:", response);

      if (response.success && response.data && response.data.Members) {
        const membersData = response.data.Members;
        setMembers(membersData);

        // Store original values for change tracking
        const originalData = {};
        membersData.forEach((member) => {
          originalData[member.MemberCode] = { ...member };
        });
        setOriginalMembers(originalData);

        console.log("👥 Found members data from API:", membersData);

        // PRIMARY MEMBER: Always create from profile (logged-in user as Passenger 1)
        console.log(
          "👤 Creating primary member from profile data (always Passenger 1)"
        );
        const primaryMember = {
          MemberCode:
            loginTokenInfo?.checkPartyCode ||
            loginTokenInfo?.cpr ||
            profileData?.CPR ||
            "POLICY_HOLDER",
          CPR: loggedInUserData.cprNumber,
          FullName: loggedInUserData.fullName,
          Gender:
            loggedInUserData.gender === "Male"
              ? "M"
              : loggedInUserData.gender === "Female"
              ? "F"
              : "M",
          DateOfBirthStr: loggedInUserData.dateOfBirth,
          PhoneNumber: loggedInUserData.mobile,
          Nationality: loggedInUserData.nationality,
          EmailAddress: loggedInUserData.email,
          isLoggedInUser: true, // Flag to indicate this is the logged-in user
        };

        // FAMILY MEMBERS: Use ALL members from API (they are the additional passengers)
        console.log("👨‍👩‍👧‍👦 Using all API members as family members (editable)");

        // Process members data (no processing needed, just pass through)
        const processedMembers = membersData.map((member) => ({
          ...member,
          // Keep original values - SelectInput will handle nationality mapping
          _debug: {
            originalNationality: member.Nationality,
            memberCode: member.MemberCode,
          },
        }));

        setPrimaryMember(primaryMember);
        setFamilyMembers(processedMembers); // All API members are family members

        console.log("✅ Setup complete:");
        console.log(
          "Primary member (Passenger 1 - logged-in user):",
          primaryMember
        );
        console.log("Family members (Passengers 2+ - from API):", membersData);
      } else {
        console.log("📋 No members found, only showing logged-in user");

        // Only logged-in user, no family members
        const primaryMember = {
          MemberCode:
            loginTokenInfo?.checkPartyCode ||
            loginTokenInfo?.cpr ||
            profileData?.CPR ||
            "POLICY_HOLDER",
          CPR: loggedInUserData.cprNumber,
          FullName: loggedInUserData.fullName,
          Gender:
            loggedInUserData.gender === "Male"
              ? "M"
              : loggedInUserData.gender === "Female"
              ? "F"
              : "M",
          DateOfBirthStr: loggedInUserData.dateOfBirth,
          PhoneNumber: loggedInUserData.mobile,
          Nationality: loggedInUserData.nationality,
          EmailAddress: loggedInUserData.email,
          isLoggedInUser: true,
        };

        setPrimaryMember(primaryMember);
        setFamilyMembers([]);

        if (response.error) {
          toast.error("Failed to load family members");
        }
      }
    } catch (error) {
      console.error("❌ Error loading travel members:", error);
      toast.error("Error loading passenger details");

      // Fallback: only show logged-in user
      const primaryMember = {
        MemberCode:
          loginTokenInfo?.checkPartyCode ||
          loginTokenInfo?.cpr ||
          profileData?.CPR ||
          "POLICY_HOLDER",
        CPR: loggedInUserData.cprNumber,
        FullName: loggedInUserData.fullName,
        Gender:
          loggedInUserData.gender === "Male"
            ? "M"
            : loggedInUserData.gender === "Female"
            ? "F"
            : "M",
        DateOfBirthStr: loggedInUserData.dateOfBirth,
        PhoneNumber: loggedInUserData.mobile,
        Nationality: loggedInUserData.nationality,
        EmailAddress: loggedInUserData.email,
        isLoggedInUser: true,
      };

      setPrimaryMember(primaryMember);
      setFamilyMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  // Handle member field changes and track modifications
  const handleMemberFieldChange = useCallback(
    (memberCode, field, value) => {
      console.log(`🔄 Field change: ${memberCode}.${field} = ${value}`);

      setFamilyMembers((prev) =>
        prev.map((member) =>
          member.MemberCode === memberCode
            ? { ...member, [field]: value }
            : member
        )
      );

      // Track which members have been modified
      const originalValue = originalMembers[memberCode]?.[field];
      if (originalValue !== value) {
        setModifiedMembers((prev) => ({
          ...prev,
          [memberCode]: {
            ...prev[memberCode],
            [field]: value,
          },
        }));
      } else {
        // Remove field from modified if it matches original
        setModifiedMembers((prev) => {
          const updated = { ...prev };
          if (updated[memberCode]) {
            delete updated[memberCode][field];
            // If no fields left for this member, remove member entirely
            if (Object.keys(updated[memberCode]).length === 0) {
              delete updated[memberCode];
            }
          }
          return updated;
        });
      }

      // Mark field as touched
      setTouchedFields((prev) => ({
        ...prev,
        [`${memberCode}_${field}`]: true,
      }));

      // Clear relevant field errors in real-time
      setFieldErrors((prev) => {
        const updated = { ...prev };
        if (field === "CPR") {
          delete updated[`${memberCode}_CPR`];
        } else if (field === "day" || field === "month" || field === "year") {
          delete updated[`${memberCode}_DateOfBirth`];
        }
        return updated;
      });
    },
    [originalMembers]
  );

  // Helper function to convert CPR nationality text to numeric code (same as contact details)
  const convertNationalityToCode = (nationalityText) => {
    console.log("[CPR Nationality Debug]", {
      input: nationalityText,
      nationalityOptionsCount: nationalityOptions.length,
      sampleOption: nationalityOptions[0],
    });

    if (!nationalityText) return "";

    // If it's already a number, return it
    if (typeof nationalityText === "number") return nationalityText;

    // Wait for nationality options to load
    if (nationalityOptions.length === 0) {
      console.warn(
        "[CPR] Nationality options not loaded yet, returning original text"
      );
      return nationalityText;
    }

    // Find the nationality option that matches the text
    // CPR might return "BHR", "BAHRAINI", "BAHRAIN", etc.
    const upperText = nationalityText.toUpperCase();
    const nationalityOption = nationalityOptions.find(
      (opt) =>
        opt.value?.toUpperCase() === upperText ||
        opt.label?.toUpperCase() === upperText ||
        opt.value?.toUpperCase().includes(upperText) ||
        upperText.includes(opt.value?.toUpperCase())
    );

    if (nationalityOption) {
      console.log(
        `[CPR] ✅ Converted nationality "${nationalityText}" to code:`,
        nationalityOption.code
      );
      return nationalityOption.code;
    }

    console.warn(
      `[CPR] ❌ Could not find nationality code for: "${nationalityText}"`
    );
    console.log(
      "[CPR] Available options:",
      nationalityOptions
        .slice(0, 5)
        .map((o) => ({ value: o.value, code: o.code }))
    );
    return nationalityText; // Return original if no match found
  };

  // Helper function to convert gender code to display format
  const getGenderFromCode = (genderCode) => {
    if (genderCode === "M") return "Male";
    if (genderCode === "F") return "Female";
    return "Male"; // Default to Male if unclear
  };

  // Parse date of birth from string
  const parseDateOfBirth = (dateStr) => {
    if (!dateStr) return { day: "", month: "", year: "" };

    try {
      // Expected format: "15/10/2023"
      const parts = dateStr.split("/");
      if (parts.length === 3) {
        return {
          day: parts[0],
          month: parts[1],
          year: parts[2],
        };
      }
    } catch (error) {
      console.error("Error parsing date:", dateStr, error);
    }

    return { day: "", month: "", year: "" };
  };

  // Format date for API
  const formatDateForAPI = (day, month, year) => {
    if (!day || !month || !year) return "";
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  };

  // Validate form fields - matching contact details validation
  const validateMemberField = (member, field, value) => {
    const errors = [];

    switch (field) {
      case "FullName":
        if (!value || !value.trim()) {
          errors.push(
            translate("This field is required.", "هذا الحقل مطلوب.")
          );
        } else if (!/^[a-zA-Z\u0600-\u06FF\s\.]+$/.test(value)) {
          errors.push(
            translate(
              "Name can only contain alphabetic characters, spaces, and periods.",
              "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
            )
          );
        }
        break;
      case "CPR":
        if (!value || value.trim().length === 0) {
          errors.push(translate("This field is required.", "هذا الحقل مطلوب."));
        } else if (value.length !== 9) {
          errors.push(
            translate(
              "CPR number must be 9 digits.",
              "يجب أن يتكون رقم الهوية الوطنية من 9 أرقام."
            )
          );
        }
        break;
      case "EmailAddress":
        if (value && !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(value)) {
          errors.push(
            translate("Invalid email address.", "عنوان البريد الإلكتروني غير صالح.")
          );
        }
        break;
      case "PhoneNumber":
        if (value && value.length !== 8) {
          errors.push(
            translate(
              "Invalid mobile number.",
              "رقم الهاتف المحمول غير صحيح."
            )
          );
        }
        break;
      case "DateOfBirth":
        if (value) {
          const birthDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (birthDate > today) {
            errors.push(
              translate(
                "Date of birth cannot be in the future.",
                "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
              )
            );
          } else if (member?.Relationship) {
            const age = Math.floor(
              (today - birthDate) / (365.25 * 24 * 60 * 60 * 1000)
            );

            if (member.Relationship === "SPOUSE" && age < 18) {
              errors.push(
                translate(
                  "Spouse must be 18 years or above.",
                  "يجب أن يكون عمر الزوج/الزوجة 18 سنة أو أكثر."
                )
              );
            } else if (member.Relationship === "CHILD") {
              const ageInMonths = Math.floor(
                (today - birthDate) / (30.44 * 24 * 60 * 60 * 1000)
              );

              if (ageInMonths < 3) {
                errors.push(
                  translate(
                    "Child must be at least 3 months old.",
                    "يجب أن يكون عمر الطفل 3 أشهر على الأقل."
                  )
                );
              } else if (age >= 18) {
                errors.push(
                  translate(
                    "Child must be under 18 years old.",
                    "يجب أن يكون عمر الطفل أقل من 18 سنة."
                  )
                );
              }
            }
          }
        }
        break;
    }

    return errors;
  };

  // Handle form submission
  const handleUpdateMembers = async () => {
    console.log("🚀 Updating travel members...");
    console.log("Modified members:", modifiedMembers);

    // Validate all modified fields
    const allErrors = {};
    Object.keys(modifiedMembers).forEach((memberCode) => {
      const memberChanges = modifiedMembers[memberCode];
      const member = familyMembers.find((m) => m.MemberCode === memberCode);

      Object.keys(memberChanges).forEach((field) => {
        const value = memberChanges[field];
        const errors = validateMemberField(member, field, value);
        if (errors.length > 0) {
          allErrors[`${memberCode}_${field}`] = errors[0];
        }
      });

      // Also validate DOB if date parts were changed
      if (memberChanges.day || memberChanges.month || memberChanges.year) {
        const dateData = parseDateOfBirth(member?.DateOfBirthStr);
        const day = memberChanges.day || dateData.day;
        const month = memberChanges.month || dateData.month;
        const year = memberChanges.year || dateData.year;

        if (day && month && year) {
          const dobStr = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
          const dobErrors = validateMemberField(member, "DateOfBirth", dobStr);
          if (dobErrors.length > 0) {
            allErrors[`${memberCode}_DateOfBirth`] = dobErrors[0];
          }
        }
      }
    });

    if (Object.keys(allErrors).length > 0) {
      setFieldErrors(allErrors);
      toast.error("Please fix validation errors");
      return;
    }

    setIsUpdating(true);
    try {
      // Prepare update payload - only send modified members with required fields
      const membersToUpdate = Object.keys(modifiedMembers).map((memberCode) => {
        const changes = modifiedMembers[memberCode];
        const member = familyMembers.find((m) => m.MemberCode === memberCode);

        const updateData = {
          MemberCode: memberCode, // Always required
          PolicyNumber: policy_id, // Always required
          ...changes, // Only the changed fields
        };

        // Handle date changes
        if (changes.day || changes.month || changes.year) {
          const currentDate = parseDateOfBirth(member.DateOfBirthStr);
          const newDay = changes.day || currentDate.day;
          const newMonth = changes.month || currentDate.month;
          const newYear = changes.year || currentDate.year;

          updateData.DateOfBirth = formatDateForAPI(newDay, newMonth, newYear);

          // Remove individual date fields
          delete updateData.day;
          delete updateData.month;
          delete updateData.year;
        }

        // Handle FullName field directly since API expects FullName, not FirstName/LastName
        if (changes.FullName) {
          updateData.FullName = changes.FullName;
        }

        return updateData;
      });

      if (membersToUpdate.length === 0) {
        toast.info("No changes to update");
        return;
      }

      console.log("📤 Sending update payload:", membersToUpdate);

      const response = await updateTravelMembers({
        PolicyNumber: policy_id,
        Members: membersToUpdate,
      });

      if (response.success) {
        toast.success("Passenger details updated successfully");

        // Clear modification tracking
        setModifiedMembers({});
        setTouchedFields({});
        setFieldErrors({});

        // Navigate back to policy overview after successful update
        setTimeout(() => {
          handleNavigationToPolicyOverview();
        }, 1500); // Give time for user to see the success message
      } else {
        console.error("❌ Failed to update members:", response.error);
        toast.error("Failed to update passenger details");
      }
    } catch (error) {
      console.error("❌ Error updating members:", error);
      toast.error("Error updating passenger details");
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle passenger removal
  const removePassenger = async (memberCode) => {
    setDeletingMemberCode(memberCode);

    try {
      console.log("🗑️ Removing passenger:", memberCode);

      const response = await deleteTravelMember(policy_id, memberCode);

      if (response.success) {
        toast.success(
          translate("Passenger removed successfully", "تم إزالة المسافر بنجاح")
        );

        // DO NOT remove from local state - backend doesn't delete immediately
        // Just navigate back to manage my policy page
        console.log("✅ Passenger delete request submitted successfully");

        // Redirect to manage my policy after showing success message
        setTimeout(() => {
          handleNavigationToPolicyOverview();
        }, 1500); // Give time for user to see the success message
      } else {
        console.error("❌ Failed to remove passenger:", response.error);
        toast.error(
          translate(
            response.error || "Failed to remove passenger",
            "فشل في إزالة المسافر"
          )
        );
      }
    } catch (error) {
      console.error("❌ Error removing passenger:", error);
      toast.error(
        translate(
          "Error removing passenger. Please try again.",
          "خطأ في إزالة المسافر. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setDeletingMemberCode(null);
    }
  };

  // Build travel data object for primary member - exactly like contact details
  const buildPrimaryMemberTravelData = () => {
    // Use profile data first (like contact details does), fallback to API member data
    const data = {
      [translate("CPR Number", "رقم الهوية الوطنية")]:
        loggedInUserData.cprNumber || primaryMember?.CPR || "",
      [translate("Full Name", "الاسم الكامل")]:
        loggedInUserData.fullName || primaryMember?.FullName || "",
      [translate("Nationality", "الجنسية")]: translate(
        loggedInUserData.nationality ||
          primaryMember?.Nationality ||
          "Bahraini",
        "بحريني"
      ),
      [translate("Gender", "الجنس")]: translate(
        // Convert single letter to full word for display
        loggedInUserData.gender === "M" ? "Male" :
        loggedInUserData.gender === "F" ? "Female" :
        loggedInUserData.gender ||
          (primaryMember?.Gender === "M"
            ? "Male"
            : primaryMember?.Gender === "F"
            ? "Female"
            : "Male"),
        loggedInUserData.gender === "M" || loggedInUserData.gender === "Male" ? "ذكر" : "أنثى"
      ),
      [translate("Date of Birth", "تاريخ الميلاد")]:
        loggedInUserData.dateOfBirth || primaryMember?.DateOfBirthStr || "",
    };

    console.log("🎯 Primary member travel data:", {
      profileData: {
        fullName: loggedInUserData.fullName,
        nationality: loggedInUserData.nationality,
        gender: loggedInUserData.gender,
        dateOfBirth: loggedInUserData.dateOfBirth,
      },
      memberData: primaryMember,
      finalData: data,
    });
    return data;
  };

  // Render logged-in user as Passenger 1 (read-only) - following contact details pattern
  const renderPrimaryMember = () => {
    if (!primaryMember) return null;

    return (
      <div className="mb-6">
        {/* Passenger Header - same as contact details */}
        <div className="mb-3 pb-3 border-b border-[#DDE2E4] flex items-center justify-between max767:flex-col max767:items-start">
          <h3 className="text-[#48535B] text-lg font-semibold">
            {translate("Passenger 1", "مسافر 1")}
          </h3>
        </div>

        {/* User Info Table - exact same structure as contact details */}
        <div className="bg-[#F6F8F9] rounded-xl p-4 max767:bg-white max767:p-0 mb-6">
          <TravelDetailsTable travelData={buildPrimaryMemberTravelData()} />
        </div>
      </div>
    );
  };

  // Helper function to get field error for a specific member and field
  const getFieldError = (memberCode, fieldName) => {
    return fieldErrors[`${memberCode}_${fieldName}`] || "";
  };

  // Handle CPR file upload with DOKI OCR - exactly like contact details
  const handleFileSelect = async (fileData, memberCode) => {
    console.log("📄 CPR file uploaded for member:", memberCode, fileData);

    // Mark field as touched
    setTouchedFields((prev) => ({
      ...prev,
      [`cpr_${memberCode}`]: true,
    }));

    // Update family member with uploaded file
    setFamilyMembers((prevMembers) => {
      return prevMembers.map((member) => {
        if (member.MemberCode === memberCode) {
          const currentFiles = member.cprFiles || [];
          const newFiles = [...currentFiles, fileData];
          return {
            ...member,
            cprFiles: newFiles,
            cprProcessing: true, // Set processing state
          };
        }
        return member;
      });
    });

    // Clear previous errors for this member's CPR field
    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      [`${memberCode}_CPR`]: "",
    }));

    // Process CPR with Vista OCR in background - exactly like contact details
    if (fileData && fileData.file) {
      try {
        console.log("Starting CPR processing for member:", memberCode);

        const result = await processTravelCPR(fileData.file);

        if (result.success && result.data?.output?.status) {
          // CPR processing successful
          console.log(
            "CPR processing completed successfully for member:",
            memberCode,
            result.data
          );

          const cprData = transformVistaCPRData(result.data);

          // Convert nationality text to code if nationality options are loaded
          const nationalityCode = convertNationalityToCode(cprData.nationality);

          // Auto-fill member data with CPR information
          setFamilyMembers((prevMembers) => {
            return prevMembers.map((member) => {
              if (member.MemberCode === memberCode) {
                return {
                  ...member,
                  // Auto-fill fields from CPR data
                  CPR: cprData.cprNumber || member.CPR,
                  FullName: cprData.fullName || member.FullName,
                  DateOfBirthStr:
                    cprData.dateOfBirth &&
                    cprData.dateOfBirth.day &&
                    cprData.dateOfBirth.month &&
                    cprData.dateOfBirth.year
                      ? `${cprData.dateOfBirth.day.padStart(
                          2,
                          "0"
                        )}/${cprData.dateOfBirth.month.padStart(2, "0")}/${
                          cprData.dateOfBirth.year
                        }`
                      : member.DateOfBirthStr,
                  Gender:
                    cprData.gender === "Male"
                      ? "M"
                      : cprData.gender === "Female"
                      ? "F"
                      : member.Gender,
                  Nationality: nationalityCode || member.Nationality,
                  // Update processing state
                  cprProcessing: false,
                  cprData: cprData,
                  cprProcessed: true,
                };
              }
              return member;
            });
          });

          // Mark relevant fields as touched for validation
          setTouchedFields((prev) => ({
            ...prev,
            [`${memberCode}_CPR`]: true,
            [`${memberCode}_FullName`]: true,
            [`${memberCode}_Gender`]: true,
            [`${memberCode}_Nationality`]: true,
          }));

          console.log(
            "✅ CPR data successfully extracted and auto-filled for member:",
            memberCode
          );
        } else {
          // CPR processing failed
          console.error(
            "CPR processing failed for member:",
            memberCode,
            result
          );

          setFamilyMembers((prevMembers) => {
            return prevMembers.map((member) => {
              if (member.MemberCode === memberCode) {
                return {
                  ...member,
                  cprProcessing: false,
                };
              }
              return member;
            });
          });

          toast.error(
            translate(
              "Failed to extract data from CPR. Please fill the form manually.",
              "فشل في استخراج البيانات من الهوية. يرجى ملء النموذج يدوياً."
            )
          );
        }
      } catch (error) {
        console.error("CPR processing error for member:", memberCode, error);

        setFamilyMembers((prevMembers) => {
          return prevMembers.map((member) => {
            if (member.MemberCode === memberCode) {
              return {
                ...member,
                cprProcessing: false,
              };
            }
            return member;
          });
        });

        toast.error(
          translate(
            "Error processing CPR document. Please try again or fill manually.",
            "خطأ في معالجة وثيقة الهوية. يرجى المحاولة مرة أخرى أو التعبئة يدوياً."
          )
        );
      }
    }
  };

  // Handle CPR file removal - exactly like contact details
  const handleFileRemove = (memberCode) => {
    console.log("📄 CPR file removed for member:", memberCode);

    // Mark field as touched
    setTouchedFields((prev) => ({
      ...prev,
      [`cpr_${memberCode}`]: true,
    }));

    // Clear CPR files for this member
    setFamilyMembers((prevMembers) => {
      return prevMembers.map((member) => {
        if (member.MemberCode === memberCode) {
          return {
            ...member,
            cprFiles: [],
            cprProcessing: false,
          };
        }
        return member;
      });
    });
  };

  // Helper function to get nationality value for SelectInput
  const getNationalityValueForSelect = (nationalityCode) => {
    if (!nationalityCode || !nationalityOptions.length) {
      console.log("🔍 Nationality lookup - missing data:", {
        code: nationalityCode,
        optionsLength: nationalityOptions.length,
      });
      return "";
    }

    console.log(
      "🔍 Looking for nationality code:",
      nationalityCode,
      "in",
      nationalityOptions.length,
      "options"
    );

    // Debug: Show first few nationality options
    console.log(
      "🔍 Sample nationality options:",
      nationalityOptions.slice(0, 10).map((opt) => ({
        code: opt.code,
        value: opt.value,
        label: opt.label,
      }))
    );

    // Find nationality option that matches the code
    const nationality = nationalityOptions.find(
      (option) => String(option.code) === String(nationalityCode)
    );

    if (nationality) {
      console.log("✅ Found nationality for code", nationalityCode, ":", {
        code: nationality.code,
        value: nationality.value,
        label: nationality.label,
      });
      return nationality.value; // Return the value that SelectInput expects
    }

    console.warn("❌ Could not find nationality for code:", nationalityCode);
    console.log(
      "❌ Available codes:",
      nationalityOptions.map((opt) => opt.code).slice(0, 20)
    );

    // Fallback: return the code as a display value so user knows what data we have
    return `Nationality Code: ${nationalityCode}`;
  };

  // Render family member form (using exact same structure as renderPassengerForm)
  const renderFamilyMemberForm = (member, index) => {
    const dateData = parseDateOfBirth(member.DateOfBirthStr);

    // Get default gender selection based on API response
    const currentGender = getGenderFromCode(member.Gender);

    // Get nationality value for SelectInput
    const nationalityValue = getNationalityValueForSelect(member.Nationality);

    console.log("🔄 Rendering member form:", {
      memberCode: member.MemberCode,
      originalNationality: member.Nationality,
      nationalityValue: nationalityValue,
      gender: currentGender,
      nationalityOptionsCount: nationalityOptions?.length || 0,
    });

    return (
      <div key={member.MemberCode} className="mb-6">
        {/* Passenger Header - exact same structure as contact details */}
        <div className="mb-3 pb-3 border-b border-[#DDE2E4] flex items-center justify-between">
          <h3 className="text-[#48535B] text-lg font-semibold">
            {translate(`Passenger ${index + 2}`, `مسافر ${index + 2}`)}
          </h3>
          <Button
            label={translate("Remove", "إزالة")}
            onSubmit={() => removePassenger(member.MemberCode)}
            className="bg-white border border-[#DDE2E4] text-[#48535B] rounded-[10px] text-center text-sm font-medium py-2 px-4 hover:bg-gray-50"
            isLoading={deletingMemberCode === member.MemberCode}
            disabled={deletingMemberCode === member.MemberCode}
          />
        </div>

        {/* CPR Upload - same as contact details */}
        <div className="mb-3">
          <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
            {translate(
              "Upload CPR (front & back)",
              "تحميل الهوية الوطنية (من الأمام والخلف)"
            )}
          </label>
          <FileUploadInput
            name={`cpr_passenger_${member.MemberCode}`}
            onFileSelect={(fileData) =>
              handleFileSelect(fileData, member.MemberCode)
            }
            onFileRemove={() => handleFileRemove(member.MemberCode)}
            onLoseFocus={() =>
              setTouchedFields((prev) => ({
                ...prev,
                [`cpr_${member.MemberCode}`]: true,
              }))
            }
            validTypes={["image/jpeg", "image/png", "application/pdf"]}
            maxSizeKB={5000}
            uploadToServer={true}
            multiple={true}
            maxFiles={2}
            disabled={member.cprProcessing}
            initialFiles={member.cprFiles || []}
          />
        </div>

        {/* CPR Number - exact same structure as contact details */}
        <div className="mb-3">
          <Input
            id={`cprNumber_${member.MemberCode}`}
            label={translate("CPR Number", "رقم الهوية الوطنية")}
            type="text"
            value={member.CPR || ""}
            handleChange={(e) =>
              handleMemberFieldChange(member.MemberCode, "CPR", e.target.value)
            }
            error={getFieldError(member.MemberCode, "CPR")}
            maxLength={9}
          />
        </div>

        {/* Full Name - exact same structure as contact details */}
        <div className="mb-3">
          <Input
            id={`fullName_${member.MemberCode}`}
            label={translate("Full Name", "الاسم الكامل")}
            type="text"
            value={member.FullName || ""}
            handleChange={(e) =>
              handleMemberFieldChange(
                member.MemberCode,
                "FullName",
                e.target.value
              )
            }
            error={getFieldError(member.MemberCode, "FullName")}
          />
        </div>

        {/* Nationality - with proper default selection */}
        <div className="mb-3">
          <SelectInput
            name="nationality"
            formData={{ nationality: nationalityValue }}
            label={translate("Nationality", "الجنسية")}
            options_data={nationalityOptions || []}
            error={getFieldError(member.MemberCode, "Nationality")}
            handleChange={(e) => {
              // Find the code for the selected nationality value
              const selectedNationality = nationalityOptions.find(
                (option) => option.value === e.target.value
              );
              const nationalityCode = selectedNationality
                ? selectedNationality.code
                : e.target.value;

              handleMemberFieldChange(
                member.MemberCode,
                "Nationality",
                nationalityCode // Store the code, not the value
              );
            }}
            isLoading={nationalitiesLoading}
            default_placeholder={translate(
              "Please select an option.",
              "يرجى اختيار خيار."
            )}
          />
        </div>

        {/* Gender - with proper default selection */}
        <div className="mb-3">
          <FormTabs
            label={translate("Gender", "الجنس")}
            tabs={genderTabs}
            onTabClick={(type) =>
              handleMemberFieldChange(
                member.MemberCode,
                "Gender",
                type === "Male" ? "M" : "F"
              )
            }
            activeTab={currentGender}
            error={getFieldError(member.MemberCode, "Gender")}
            className="!relative !z-0"
          />
        </div>

        {/* Date of Birth - with mobile native date picker */}
        <div className="date_of_birth mb-3">
          <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
            {translate("Date of Birth", "تاريخ الميلاد")}
          </label>

          {isMobile ? (
            // Mobile: Use SingleDatePicker with native date picker
            <SingleDatePicker
              value={
                dateData.day && dateData.month && dateData.year
                  ? `${dateData.year}-${dateData.month.padStart(2, "0")}-${dateData.day.padStart(2, "0")}`
                  : ""
              }
              onChange={(dateString) => {
                if (!dateString) return;
                // Parse the date string (YYYY-MM-DD format from mobile date picker)
                const [year, month, day] = dateString.split("-");

                // Update DateOfBirthStr directly so the value displays correctly
                const formattedDate = `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
                setFamilyMembers((prev) =>
                  prev.map((m) =>
                    m.MemberCode === member.MemberCode
                      ? { ...m, DateOfBirthStr: formattedDate }
                      : m
                  )
                );

                // Track modification for change detection
                handleMemberFieldChange(member.MemberCode, "day", day);
                handleMemberFieldChange(member.MemberCode, "month", month);
                handleMemberFieldChange(member.MemberCode, "year", year);

                // Clear date errors
                setFieldErrors((prev) => ({
                  ...prev,
                  [`${member.MemberCode}_DateOfBirth`]: "",
                }));
              }}
              placeholder={translate("Select Date", "اختر التاريخ")}
              error={
                getFieldError(member.MemberCode, "DateOfBirth") ||
                getFieldError(member.MemberCode, "day") ||
                getFieldError(member.MemberCode, "month") ||
                getFieldError(member.MemberCode, "year")
              }
              useNativeMobile={true}
            />
          ) : (
            // Desktop: Keep existing select dropdowns
            <div className="flex gap-6 self-stretch">
              <SelectInput
                name="day"
                options_data={daysOptions}
                formData={{ day: dateData.day || "" }}
                default_placeholder={translate("Day", "يوم")}
                error=""
                handleChange={(e) =>
                  handleMemberFieldChange(
                    member.MemberCode,
                    "day",
                    e.target.value
                  )
                }
                maxMenuHeight={185}
              />
              <SelectInput
                name="month"
                options_data={monthsOptions}
                formData={{ month: dateData.month || "" }}
                default_placeholder={translate("Month", "شهر")}
                error=""
                handleChange={(e) =>
                  handleMemberFieldChange(
                    member.MemberCode,
                    "month",
                    e.target.value
                  )
                }
                maxMenuHeight={185}
              />
              <SelectInput
                name="year"
                options_data={yearsOptions}
                formData={{ year: dateData.year || "" }}
                default_placeholder={translate("Year", "سنة")}
                error=""
                handleChange={(e) =>
                  handleMemberFieldChange(
                    member.MemberCode,
                    "year",
                    e.target.value
                  )
                }
                maxMenuHeight={185}
              />
            </div>
          )}

          {/* Show date error if any (desktop only - mobile SingleDatePicker shows its own) */}
          {!isMobile &&
            (getFieldError(member.MemberCode, "DateOfBirth") ||
              getFieldError(member.MemberCode, "day") ||
              getFieldError(member.MemberCode, "month") ||
              getFieldError(member.MemberCode, "year")) && (
              <div className="text-red-600 text-sm mt-1">
                {getFieldError(member.MemberCode, "DateOfBirth") ||
                  getFieldError(member.MemberCode, "day") ||
                  getFieldError(member.MemberCode, "month") ||
                  getFieldError(member.MemberCode, "year")}
              </div>
            )}
        </div>

        {/* Relationship */}
        <div className="mb-3">
          <SelectInput
            name="relationship"
            formData={{ relationship: member.Relationship || "" }}
            label={translate("Relationship", "العلاقة")}
            options_data={relationshipOptions}
            error={getFieldError(member.MemberCode, "Relationship")}
            handleChange={(e) =>
              handleMemberFieldChange(
                member.MemberCode,
                "Relationship",
                e.target.value
              )
            }
            default_placeholder={translate(
              "Please select relationship",
              "يرجى اختيار العلاقة"
            )}
          />
        </div>
      </div>
    );
  };

  const hasChanges = Object.keys(modifiedMembers).length > 0;

  if (membersLoading) {
    return (
      <PolicyCancellationMotorLayout
        is_hide_back_btn={false}
        show_close_btn={false}
        isFirstStep={true}
        backClickHandler={handleNavigationToPolicyOverview}
        handleCloseBtnClick={handleNavigationToPolicyOverview}
        is_bottom_sticky_layout={false}
      >
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00719A] mx-auto mb-4"></div>
            <p className="text-gray-600">
              {translate(
                "Loading passenger details...",
                "جاري تحميل تفاصيل المسافرين..."
              )}
            </p>
          </div>
        </div>
      </PolicyCancellationMotorLayout>
    );
  }

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      show_close_btn={false}
      isFirstStep={true}
      backClickHandler={handleNavigationToPolicyOverview}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      is_bottom_sticky_layout={false}
    >
      <TravelBottomStickyBtnLayout
        btn_onClick={handleUpdateMembers}
        btn_disabled={!hasChanges || isUpdating}
        btn_label={translate(
          isUpdating ? "Updating..." : "Confirm",
          isUpdating ? "جاري التحديث..." : "تأكيد"
        )}
        isLoading={isUpdating}
      >
        <div className="flex flex-col items-center p-6 max767:pt-0 max767:p-5 !pb-28">
          <div className="w-[33%] max-350:w-[100%] max767:w-[100%] ipad:w-[55%]">
            <TitleDescription
              title={translate(
                "Modify Passenger Details",
                "تغيير بيانات المسافر"
              )}
              description={translate(
                "Please provide the necessary information below.",
                "يرجى تقديم المعلومات اللازمة أدناه."
              )}
            />

            {/* Primary Member Section */}
            {renderPrimaryMember()}

            {/* Family Members Section */}
            {familyMembers.length > 0 && (
              <div>
                {familyMembers.map((member, index) =>
                  renderFamilyMemberForm(member, index)
                )}
              </div>
            )}

            {/* No additional passengers message */}
            {familyMembers.length === 0 && primaryMember && (
              <div className="text-center py-8 border-t border-[#DDE2E4] pt-6">
                <p className="text-lg text-[#48535B]">
                  {translate(
                    "No additional passengers found on this policy.",
                    "لا يوجد مسافرون إضافيون في هذه الوثيقة."
                  )}
                </p>
                <p className="text-sm text-[#5B6871] mt-2">
                  {translate(
                    "Only Passenger 1 (policy holder) information is available.",
                    "فقط معلومات المسافر 1 (حامل الوثيقة) متاحة."
                  )}
                </p>
              </div>
            )}

            {/* No data at all */}
            {familyMembers.length === 0 && !primaryMember && (
              <div className="text-center py-8">
                <p className="text-lg text-[#48535B]">
                  {translate(
                    "No passenger details found for this policy.",
                    "لم يتم العثور على تفاصيل المسافرين لهذه الوثيقة."
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </TravelBottomStickyBtnLayout>
    </PolicyCancellationMotorLayout>
  );
};

export default ModifyPassengerTravel;
